import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Pagination,
  Row,
  Skeleton,
  Space,
  Typography,
} from 'antd';

import {
  Error403Unauthorized,
  LogsGenerator,
  MultipleSelect,
} from '@app/components';
import {
  ACTIONS_HISTORY,
  DATE_PICKER_FORMAT,
  PREFIX,
  rangePickerLocale,
} from '@app/constants';
import { generateSpanWithDataTestId } from '@app/shared/utils';

import {
  useActionsHistory,
  useActionsHistoryEffects,
} from '@pages/Users/ActionsHistoryPage/hooks';

import '@pages/Users/ActionsHistoryPage/styles/ActionsHistoryPage.desktop.scss';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const ActionsHistoryPage = () => {
  const {
    fetchUserHistoryFilterData,
    logs,
    mainContentLoader,
    rowsPerPage,
    page,
    filters,
    filtersLoader,
    selectedActions,
    setSelectedActions,
    selectedUsers,
    setSelectedUsers,
    searchButtonLoader,
    resetButtonLoader,
    isImportant,
    setImportant,
    datesRange,
    setDatesRange,
    disabledDate,
    logsTotal,
    isMobile,
    fetchUserHistory,
    onShowSizeChange,
    handleNames,
    runSearch,
    clearFilters,
    changePage,
    getButtonWidth,
    isThirdBreakpoint,
    isSecondBreakpoint,
    isFirstBreakpoint,
    isMobileFilters,
    areFiltersOpened,
    toggleFilters,
    actionsWithHandledLabels,
    show403Unauthorized,
    forbidden403PageTitle,
    showFilters,
  } = useActionsHistory();

  useActionsHistoryEffects({ fetchUserHistoryFilterData, fetchUserHistory });

  if (show403Unauthorized) {
    return <Error403Unauthorized title={forbidden403PageTitle} />;
  }

  return (
    <div className={`${PREFIX}${ACTIONS_HISTORY}`}>
      <Space className="filters-panel" size="large" direction="vertical">
        {isMobileFilters && (
          <>
            <Button
              block
              type={areFiltersOpened ? 'primary' : 'default'}
              icon={
                <FontAwesomeIcon
                  icon={areFiltersOpened ? faChevronUp : faChevronDown}
                />
              }
              onClick={toggleFilters}
            >
              {areFiltersOpened ? 'Скрыть фильтры' : 'Показать фильтры'}
            </Button>
          </>
        )}

        {showFilters && (
          <Space className="filters-panel" direction="vertical">
            <Row>
              <Col
                span={isThirdBreakpoint ? 16 : 24}
                style={{ width: isThirdBreakpoint ? 'auto' : '100%' }}
              >
                <Row align="middle">
                  <Space
                    direction={isThirdBreakpoint ? 'horizontal' : 'vertical'}
                    style={{ width: isThirdBreakpoint ? 'auto' : '100%' }}
                  >
                    <div className="period-label">
                      <Text data-testid="period-title">Период:</Text>
                    </div>
                    <RangePicker
                      locale={rangePickerLocale}
                      classNames={{ popup: { root: 'dates-range calendars' } }}
                      allowClear={false}
                      format={DATE_PICKER_FORMAT}
                      disabledDate={disabledDate}
                      style={{
                        width: isThirdBreakpoint ? '303px' : '100%',
                      }}
                      value={datesRange}
                      onChange={(values) => setDatesRange(values)}
                    />
                  </Space>
                </Row>
              </Col>
              <Col
                span={isThirdBreakpoint ? 8 : 24}
                style={{ display: isThirdBreakpoint ? 'block' : 'none' }}
              />
            </Row>

            <Row justify="space-between" wrap>
              <Col span={isFirstBreakpoint ? 16 : 24}>
                <Space
                  className="bottom-filters-container"
                  direction={isSecondBreakpoint ? 'horizontal' : 'vertical'}
                  style={{
                    minWidth: isSecondBreakpoint ? '570px' : '100%',
                  }}
                >
                  <Space
                    direction={isThirdBreakpoint ? 'horizontal' : 'vertical'}
                    style={{ width: isThirdBreakpoint ? 'auto' : '100%' }}
                  >
                    <MultipleSelect
                      loader={filtersLoader}
                      placeholder="Все пользователи"
                      allOptions={(filters?.Users || []).map(
                        ({ Id, NameLast, NameFirst }) => ({
                          value: Id,
                          label: handleNames(NameLast, NameFirst),
                        })
                      )}
                      defaultSelected={true}
                      selectedOption={selectedUsers}
                      setSelectedOptions={setSelectedUsers}
                      dataTestId="all-users-input"
                      dropdownDataTestId="user-dropdown"
                      optionDataTestId="user-item"
                    />
                    <MultipleSelect
                      loader={filtersLoader}
                      placeholder="Все действия"
                      allOptions={actionsWithHandledLabels}
                      selectedOption={selectedActions}
                      setSelectedOptions={setSelectedActions}
                      dataTestId="all-actions-input"
                      dropdownDataTestId="action-dropdown"
                      optionDataTestId="action-item"
                    />
                  </Space>
                  <Checkbox
                    checked={isImportant}
                    onChange={() => setImportant((status) => !status)}
                    data-testid={
                      isImportant
                        ? 'important-actions-checkbox'
                        : 'important-actions-checkbox-unchecked'
                    }
                  >
                    <Text data-testid="important-actions-text">
                      Только важные действия
                    </Text>
                  </Checkbox>
                </Space>
              </Col>

              <Col
                span={isFirstBreakpoint ? 8 : 24}
                style={{ marginTop: isFirstBreakpoint ? 'auto' : '8px' }}
              >
                <div
                  className="top-buttons-container"
                  style={{
                    flexDirection: isThirdBreakpoint ? 'row' : 'column',
                    justifyContent: isFirstBreakpoint
                      ? 'flex-end'
                      : 'flex-start',
                  }}
                >
                  <div className="top-button">
                    <Button
                      loading={resetButtonLoader}
                      block
                      icon={<CloseCircleOutlined />}
                      style={{
                        width: getButtonWidth(),
                      }}
                      onClick={clearFilters}
                      data-testid="reset-filters"
                    >
                      Сброс
                    </Button>
                  </div>
                  <div className="top-button">
                    <Button
                      loading={searchButtonLoader}
                      block
                      icon={<FilterOutlined />}
                      type="primary"
                      style={{
                        width: getButtonWidth(),
                        marginTop: isThirdBreakpoint ? 'auto' : '8px',
                      }}
                      onClick={() =>
                        runSearch(
                          selectedUsers,
                          selectedActions,
                          isImportant,
                          datesRange
                        )
                      }
                      data-testid="search-by-filters"
                    >
                      Поиск
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Space>
        )}

        {mainContentLoader ? (
          <Skeleton active />
        ) : (
          <>
            {logs.length > 0 ? (
              <LogsGenerator
                actions={logs.map(({ ...log }, index) => ({
                  key: log.Timestamp + log.ActionId + index,
                  ...log,
                }))}
                isEmployee={false}
              />
            ) : (
              <Empty
                description={generateSpanWithDataTestId(
                  'Нет данных',
                  'no-data'
                )}
              />
            )}

            {logs.length > 0 && logsTotal !== null && (
              <Pagination
                current={page}
                pageSize={rowsPerPage}
                total={logsTotal}
                onShowSizeChange={onShowSizeChange}
                size={isMobile ? 'small' : 'default'}
                showSizeChanger={true}
                onChange={(page, pageSize) => {
                  changePage(page, pageSize);
                }}
                showTotal={(total, range) =>
                  `Показаны ${range[0]}-${range[1]} из ${total}`
                }
              />
            )}
          </>
        )}
      </Space>
    </div>
  );
};
